import { FC, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Header } from 'components';
import Table from 'components/tables/table';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import useApi from 'utils/use-api';
import useTableData from 'utils/use-table-data';
import { Role } from 'types/role';
import dayjs from 'dayjs';
import { useUrlState, withPermissions } from 'utils';
import CreateRole from './create';
import UpdateRole from './update';
import DeleteRole from './delete';
import ViewRole from './view';

const RolesPage: FC = () => {
  const api = useApi();
  const [skip, setSkip] = useState(0);
  const [limit] = useUrlState<number>('pageSize');
  const [modal, setModal] = useUrlState<string>('modal');
  const [selectedId, setSelectedId] = useUrlState<number>('id');
  const [searchTerm, setSearchTerm] = useUrlState<string>('search');

  const {
    data: rolesResponse,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['roles', skip, limit, searchTerm],
    queryFn: async () => {
      const res = await api.get('/roles', { params: { skip, limit: limit || 10 } });
      let rows = res.data as Role[];
      if (searchTerm) {
        rows = rows.filter(role => role.name.toLowerCase().includes(searchTerm.toLowerCase()));
      }
      return { rows, total: rows.length };
    },
  });

  const rolesData = useTableData({
    rows: rolesResponse?.rows || [],
    count: rolesResponse?.total || 0,
  });

  const TableColumns = () => (
    <tr>
      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Name</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Description</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Permissions</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Updated</th>
      <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
        <span className="sr-only">Actions</span>
      </th>
    </tr>
  );

  const TableRow = (role: Role) => (
    <tr className="hover:bg-gray-50">
      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{role.name}</td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{role.description || '—'}</td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
        <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-500/10">
          {role.permissions?.length || 0} perms
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{role.updatedAt ? dayjs(role.updatedAt).format('MMM DD, YYYY') : '—'}</td>
      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 space-x-3">
        {withPermissions(['role:update'])(
          <button
            onClick={() => {
              setSelectedId(role.id as any);
              setModal('update' as any);
            }}
            className="inline-flex items-center gap-x-1 text-primary-600 hover:text-primary-900"
          >
            <PencilSquareIcon className="h-4 w-4" />
            Edit
          </button>,
        )}
        {withPermissions(['role:delete'])(
          <button
            onClick={() => {
              setSelectedId(role.id as any);
              setModal('delete' as any);
            }}
            className="inline-flex items-center gap-x-1 text-red-600 hover:text-red-900"
          >
            <TrashIcon className="h-4 w-4" />
            Delete
          </button>,
        )}
      </td>
    </tr>
  );

  const renderSearch = (
    <div className="w-full flex justify-between items-center mb-4">
      <input
        type="text"
        className="block w-full sm:w-80 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
        placeholder="Search roles"
        value={searchTerm || ''}
        onChange={e => {
          setSearchTerm(e.target.value as any);
          setSkip(0);
        }}
      />
    </div>
  );

  return (
    <main className="flex-1 flex flex-col overflow-hidden h-screen bg-gray-50">
      <Header
        renderActions={() => (
          <>
            {withPermissions(['role:create'])(
              <button
                type="button"
                onClick={() => setModal('create' as any)}
                className="inline-flex items-center gap-x-2 rounded-md bg-primary-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              >
                <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                New Role
              </button>,
            )}
          </>
        )}
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 min-h-full mx-auto p-4 sm:p-6 overflow-y-auto bg-gray-50">
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12 lg:gap-8">
            <div className="col-span-12">
              <Table
                title="Roles"
                data={rolesData || { rows: [], total: 0, page: 1, pageSize: limit || 10, totalPages: 0 }}
                loading={isLoading}
                isRefetching={isRefetching}
                skip={skip}
                limit={limit || 10}
                setSkip={setSkip}
                showTableHeader={true}
                hasSearch={false}
                renderHeaderItems={() => renderSearch}
                refetch={refetch}
                renderColumns={TableColumns}
                renderItem={TableRow}
              />
            </div>
          </div>
        </div>
      </div>

      {modal === ('create' as any) && (
        <CreateRole
          open={true}
          setOpen={v => {
            if (!v) setModal(undefined as any);
          }}
          onSuccess={() => {
            refetch();
          }}
        />
      )}
      {modal === ('view' as any) && selectedId && (
        <ViewRole
          open={true}
          setOpen={v => {
            if (!v) {
              setModal(undefined as any);
              setSelectedId(undefined as any);
            }
          }}
          role={rolesResponse?.rows.find(r => r.id === Number(selectedId))}
        />
      )}
      {modal === ('update' as any) && selectedId && (
        <UpdateRole
          open={true}
          setOpen={v => {
            if (!v) {
              setModal(undefined as any);
              setSelectedId(undefined as any);
            }
          }}
          role={rolesResponse?.rows.find(r => r.id === Number(selectedId))}
          onSuccess={() => refetch()}
        />
      )}
      {modal === ('delete' as any) && selectedId && (
        <DeleteRole
          open={true}
          setOpen={v => {
            if (!v) {
              setModal(undefined as any);
              setSelectedId(undefined as any);
            }
          }}
          roleId={Number(selectedId)}
          onSuccess={() => refetch()}
        />
      )}
    </main>
  );
};

export default RolesPage;
